import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { EarnedAchievement, LockedAchievement } from '@/redux/thunks/achievementThunks';
import './AchievementDetailModal.scss';

interface Props {
  /** Pass either an earned or locked achievement; `null` closes the modal */
  achievement: EarnedAchievement | LockedAchievement | null;
  /** Pre-formatted earn amount (e.g. "+3 KLV"); `null` hides the row */
  earnDisplay?: string | null;
  onClose: () => void;
  explorerBaseUrl?: string;
}

const isEarned = (a: any): a is EarnedAchievement => a && 'awardedAt' in a;

const formatRarity = (pct: number): string => {
  if (pct === 0) return '—';
  if (pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
};

const rarityLabel = (pct: number): string => {
  if (pct === 0) return 'Brand new';
  if (pct <= 5) return 'Mythic';
  if (pct <= 25) return 'Rare';
  if (pct <= 50) return 'Uncommon';
  return 'Common';
};

const rarityClass = (pct: number): string => {
  if (pct <= 5) return 'mythic';
  if (pct <= 25) return 'rare';
  if (pct <= 50) return 'uncommon';
  return 'common';
};

const formatAwardedDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const AchievementDetailModal: React.FC<Props> = ({
  achievement,
  earnDisplay,
  onClose,
  explorerBaseUrl,
}) => {
  // Close on Escape; lock body scroll while open
  useEffect(() => {
    if (!achievement) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [achievement, onClose]);

  if (!achievement) return null;

  const earned = isEarned(achievement);
  const rarity = achievement.rarityPct;

  return (
    <div
      className="ach-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ach-modal-title"
      onClick={onClose}
    >
      <div
        className={[
          'ach-modal',
          earned ? 'ach-modal--earned' : 'ach-modal--locked',
          `ach-modal--rarity-${rarityClass(rarity)}`,
        ].join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="ach-modal__close"
          onClick={onClose}
          aria-label="Close achievement details"
        >
          ×
        </button>

        <div className="ach-modal__hero">
          <div className="ach-modal__halo" />
          <div className="ach-modal__icon">{achievement.icon}</div>
          {!earned && <div className="ach-modal__lock-badge">🔒 Locked</div>}
        </div>

        <h2 id="ach-modal-title" className="ach-modal__name">
          {achievement.name}
        </h2>

        <p className="ach-modal__description">{achievement.description}</p>

        <div className="ach-modal__stats">
          <div className="ach-modal__stat">
            <span className="ach-modal__stat-label">Rarity</span>
            <span className={`ach-modal__stat-value ach-modal__stat-value--${rarityClass(rarity)}`}>
              {formatRarity(rarity)} <small>· {rarityLabel(rarity)}</small>
            </span>
          </div>
          <div className="ach-modal__stat">
            <span className="ach-modal__stat-label">Holders</span>
            <span className="ach-modal__stat-value">{achievement.holders.toLocaleString()}</span>
          </div>
          {earned && (
            <div className="ach-modal__stat">
              <span className="ach-modal__stat-label">Earned</span>
              <span className="ach-modal__stat-value">
                {formatAwardedDate((achievement as EarnedAchievement).awardedAt)}
              </span>
            </div>
          )}
          {earnDisplay && (
            <div className="ach-modal__stat">
              <span className="ach-modal__stat-label">
                {earned ? 'Reward' : 'Reward on unlock'}
              </span>
              <span className="ach-modal__stat-value ach-modal__stat-value--earn">
                {earnDisplay}
              </span>
            </div>
          )}
        </div>

        {earned && (achievement as EarnedAchievement).txHash && (
          <a
            className="ach-modal__onchain"
            href={`${explorerBaseUrl ?? 'https://testnet.kleverscan.org/transaction'}/${(achievement as EarnedAchievement).txHash}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            ✓ View on-chain proof
          </a>
        )}

        {!earned && (
          <div className="ach-modal__howto">
            <span className="ach-modal__howto-label">How to earn</span>
            <p className="ach-modal__howto-text">{(achievement as LockedAchievement).howTo}</p>
            <Link to="/dashboard" className="ach-modal__cta" onClick={onClose}>
              Get started →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AchievementDetailModal;
